import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { X, DollarSign, Tag, FileText, Calendar, User, ArrowUpRight, ArrowDownRight } from 'lucide-react'
import { getColors } from '../../../theme/colors'
import { createEntry, updateEntry } from '../../../store/slices/cashDayBookSlice'

const EntryModal = ({ onClose, categories, employees, isAdmin, editingEntry = null }) => {
  const dispatch = useDispatch()
  const { loading, activeCategory, activeType } = useSelector(state => state.cashDayBook)
  const colors = getColors()

  const [formData, setFormData] = useState({
    category: editingEntry?.category || activeCategory || '',
    type: editingEntry?.type || activeType || 'Income',
    amount: editingEntry?.amount || '',
    description: editingEntry?.description || '',
    date: editingEntry?.date ? new Date(editingEntry.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
    userId: editingEntry?.user?._id || ''
  })

  // Set isNewCategory to false if we have a pre-selected or existing category
  const [isNewCategory, setIsNewCategory] = useState(false)

  const handleInputChange = e => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleCategoryChange = e => {
    const value = e.target.value
    if (value === '__new__') {
      setIsNewCategory(true)
      setFormData(prev => ({ ...prev, category: '' }))
    } else {
      setIsNewCategory(false)
      setFormData(prev => ({ ...prev, category: value }))
    }
  }

  const handleSubmit = async e => {
    e.preventDefault()

    if (!formData.category || !formData.amount || !formData.date) {
      return
    }

    const submitData = {
      ...formData,
      amount: parseFloat(formData.amount)
    }

    // Only include userId if admin is creating for someone else
    if (isAdmin && formData.userId) {
      submitData.userId = formData.userId
    }

    try {
      if (editingEntry) {
        await dispatch(updateEntry({ id: editingEntry._id, data: submitData })).unwrap()
      } else {
        await dispatch(createEntry(submitData)).unwrap()
      }
      onClose()
    } catch (error) {
      // Error is handled by the slice
    }
  }

  return (
    <div className='fixed inset-0 bg-[#0a0a0a]/90 backdrop-blur-xl flex items-center justify-center z-50 p-4'>
      <div
        className={`${colors.cardBg} ${colors.cardBgOpacity} border ${colors.cardBorder} rounded-3xl p-8 w-full max-w-lg shadow-[0_32px_64px_-12px_rgba(0,0,0,0.8)] relative overflow-hidden`}
      >
        {/* Decorative Background */}
        <div className={`absolute -top-24 -right-24 w-48 h-48 rounded-full blur-[100px] opacity-20 ${formData.type === 'Income' ? 'bg-emerald-500' : 'bg-red-500'}`} />

        {/* Header */}
        <div className='flex items-center justify-between mb-8 relative'>
          <div>
            <h2 className={`text-3xl font-black ${colors.textPrimary} tracking-tight`}>
              {editingEntry ? 'Edit Transaction' : 'New Transaction'}
            </h2>
            <p className={`${colors.textMuted} text-sm mt-1 uppercase tracking-widest font-bold`}>
              {formData.type} Record • {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </p>
          </div>
          <button
            onClick={onClose}
            className={`p-2 rounded-full hover:bg-white/5 ${colors.textSecondary} hover:${colors.textPrimary} transition-all duration-300`}
          >
            <X size={28} />
          </button>
        </div>

        <div className='space-y-6 relative'>
          {/* User Selection (Admin Only) */}
          {isAdmin && (
            <div className='group'>
              <label className={`block ${colors.textMuted} text-[11px] font-black uppercase tracking-[0.2em] mb-2.5`}>
                Assign To Entity
              </label>
              <div className='relative'>
                <User className='absolute left-4 top-1/2 -translate-y-1/2 text-gray-500' size={18} />
                <select
                  name='userId'
                  value={formData.userId}
                  onChange={handleInputChange}
                  className={`w-full pl-12 pr-4 py-4 ${colors.inputBg} border ${colors.inputBorder} rounded-2xl ${colors.textPrimary} focus:border-emerald-500/50 focus:ring-4 focus:ring-emerald-500/10 transition-all appearance-none font-medium`}
                >
                  <option value=''>Personal Business (Default)</option>
                  {employees.map(employee => (
                    <option key={employee._id} value={employee._id}>
                      {employee.username} ({employee.employeeData?.designation || employee.role})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Type Toggle */}
          <div>
            <label className={`block ${colors.textMuted} text-[11px] font-black uppercase tracking-[0.2em] mb-2.5`}>
              Transaction Direction
            </label>
            <div className='flex p-1.5 bg-black/40 rounded-2xl border border-white/5'>
              <button
                type='button'
                onClick={() => setFormData(p => ({ ...p, type: 'Income' }))}
                className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-[14px] text-sm font-black transition-all duration-500 ${formData.type === 'Income' ? 'bg-emerald-500 text-white shadow-lg' : 'text-gray-500 hover:text-gray-300'}`}
              >
                <ArrowUpRight size={18} />
                INCOME
              </button>
              <button
                type='button'
                onClick={() => setFormData(p => ({ ...p, type: 'Expense' }))}
                className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-[14px] text-sm font-black transition-all duration-500 ${formData.type === 'Expense' ? 'bg-red-500 text-white shadow-lg' : 'text-gray-500 hover:text-gray-300'}`}
              >
                <ArrowDownRight size={18} />
                EXPENSE
              </button>
            </div>
          </div>

          <div className='grid grid-cols-2 gap-6'>
            {/* Amount */}
            <div>
              <label className={`block ${colors.textMuted} text-[11px] font-black uppercase tracking-[0.2em] mb-2.5`}>
                Value (PKR)
              </label>
              <div className='relative'>
                <span className='absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-bold'>₨</span>
                <input
                  type='number'
                  name='amount'
                  value={formData.amount}
                  onChange={handleInputChange}
                  placeholder='0.00'
                  className={`w-full pl-10 pr-4 py-4 ${colors.inputBg} border ${colors.inputBorder} rounded-2xl ${colors.textPrimary} focus:border-emerald-500/50 focus:ring-4 focus:ring-emerald-500/10 transition-all font-black text-xl`}
                  required
                />
              </div>
            </div>

            {/* Date */}
            <div>
              <label className={`block ${colors.textMuted} text-[11px] font-black uppercase tracking-[0.2em] mb-2.5`}>
                Effective Date
              </label>
              <div className='relative'>
                <Calendar className='absolute left-4 top-1/2 -translate-y-1/2 text-gray-500' size={18} />
                <input
                  type='date'
                  name='date'
                  value={formData.date}
                  onChange={handleInputChange}
                  className={`w-full pl-12 pr-4 py-4 ${colors.inputBg} border ${colors.inputBorder} rounded-2xl ${colors.textPrimary} focus:border-emerald-500/50 focus:ring-4 focus:ring-emerald-500/10 transition-all font-medium`}
                  required
                />
              </div>
            </div>
          </div>

          {/* Category */}
          <div>
            <label className={`block ${colors.textMuted} text-[11px] font-black uppercase tracking-[0.2em] mb-2.5`}>
              Classification
            </label>
            {!isNewCategory ? (
              <div className='relative'>
                <Tag className='absolute left-4 top-1/2 -translate-y-1/2 text-gray-500' size={18} />
                <select
                  name='category'
                  value={formData.category}
                  onChange={handleCategoryChange}
                  className={`w-full pl-12 pr-4 py-4 ${colors.inputBg} border ${colors.inputBorder} rounded-2xl ${colors.textPrimary} focus:border-emerald-500/50 focus:ring-4 focus:ring-emerald-500/10 transition-all appearance-none font-medium`}
                  required
                >
                  <option value=''>Select Category</option>
                  {categories.map(category => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                  <option value='__new__' className='text-emerald-400 font-bold'>+ Create New Classification</option>
                </select>
              </div>
            ) : (
              <div className='flex gap-2 animate-in slide-in-from-left duration-300'>
                <div className='relative flex-1'>
                  <Tag className='absolute left-4 top-1/2 -translate-y-1/2 text-emerald-500' size={18} />
                  <input
                    type='text'
                    name='category'
                    value={formData.category}
                    onChange={handleInputChange}
                    placeholder='Class name (e.g., Kitchen, Logistics)'
                    className={`w-full pl-12 pr-4 py-4 ${colors.inputBg} border-emerald-500/50 border rounded-2xl ${colors.textPrimary} focus:ring-4 focus:ring-emerald-500/10 transition-all font-bold placeholder:font-normal`}
                    required
                    autoFocus
                  />
                </div>
                <button
                  type='button'
                  onClick={() => {
                    setIsNewCategory(false)
                    setFormData(prev => ({ ...prev, category: '' }))
                  }}
                  className={`p-4 ${colors.cardBg} border ${colors.cardBorder} rounded-2xl text-red-400 hover:bg-red-500/10 transition-all`}
                >
                  <X size={20} />
                </button>
              </div>
            )}
          </div>

          {/* Description */}
          <div>
            <label className={`block ${colors.textMuted} text-[11px] font-black uppercase tracking-[0.2em] mb-2.5`}>
              Narrative (Optional)
            </label>
            <div className='relative'>
              <FileText className='absolute left-4 top-4 text-gray-500' size={18} />
              <textarea
                name='description'
                value={formData.description}
                onChange={handleInputChange}
                placeholder='Provide details about this transaction...'
                rows='3'
                className={`w-full pl-12 pr-4 py-4 ${colors.inputBg} border ${colors.inputBorder} rounded-2xl ${colors.textPrimary} focus:border-emerald-500/50 focus:ring-4 focus:ring-emerald-500/10 transition-all resize-none font-medium`}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className='flex gap-4 pt-4'>
            <button
              type='button'
              onClick={onClose}
              className={`flex-1 py-4 px-6 rounded-2xl font-bold ${colors.textSecondary} hover:${colors.textPrimary} hover:bg-white/5 transition-all duration-300`}
            >
              Discard
            </button>
            <button
              type='button'
              onClick={handleSubmit}
              disabled={loading.creating}
              className={`flex-[2] py-4 px-6 rounded-2xl font-black text-white bg-gradient-to-r ${formData.type === 'Income' ? 'from-emerald-600 to-teal-600' : 'from-red-600 to-orange-600'} shadow-xl hover:shadow-2xl hover:scale-[1.02] active:scale-95 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {loading.creating ? (
                <div className='flex items-center justify-center gap-3'>
                  <div className='w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin' />
                  PROCESSING...
                </div>
              ) : (
                editingEntry ? 'SAVE CHANGES' : `COMMIT ${formData.type.toUpperCase()}`
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EntryModal
